import { AlertTriangle, CheckCircle2, Sparkles, Target } from 'lucide-react'
import { getApiKey } from '../api'

export type Recommendation = {
  title: string
  why: string
  action: string
  priority: 1 | 2 | 3
  metric: string | null
}

export type CoachingPlan = {
  vici_user: string
  tier: number
  headline: string
  recommendations: Recommendation[]
  strengths: string[]
  next_promotion_blockers: string[]
  generated_at: string
}

// Best-effort fetch — 404 / network error → null so the section silently
// hides if the backend route isn't deployed yet (same pattern as scorecard).
export async function fetchCoaching(
  viciUser: string
): Promise<CoachingPlan | null> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const key = getApiKey()
  if (key) headers.Authorization = `Bearer ${key}`
  let res: Response
  try {
    res = await fetch(
      `/api/agents/${encodeURIComponent(viciUser)}/coaching`,
      { headers }
    )
  } catch {
    return null
  }
  if (!res.ok) return null
  try {
    return (await res.json()) as CoachingPlan
  } catch {
    return null
  }
}

const PRIORITY_LABELS: Record<1 | 2 | 3, string> = {
  1: 'Critical',
  2: 'Important',
  3: 'Nice-to-have',
}

// Tailwind class strings for each priority bucket. Kept verbose so the JIT
// extractor picks them up at build time (no dynamic class concatenation).
const PRIORITY_STYLES: Record<
  1 | 2 | 3,
  { border: string; badge: string; heading: string }
> = {
  1: {
    border: 'border-red-700/70',
    badge: 'bg-red-950/60 text-red-300 border border-red-800',
    heading: 'text-red-300',
  },
  2: {
    border: 'border-amber-700/70',
    badge: 'bg-amber-950/60 text-amber-300 border border-amber-800',
    heading: 'text-amber-300',
  },
  3: {
    border: 'border-slate-700',
    badge: 'bg-slate-900 text-slate-300 border border-slate-700',
    heading: 'text-slate-300',
  },
}

export function CoachingTab({ plan }: { plan: CoachingPlan }) {
  const groups: Array<{ priority: 1 | 2 | 3; recs: Recommendation[] }> = (
    [1, 2, 3] as const
  ).map((p) => ({
    priority: p,
    recs: plan.recommendations.filter((r) => r.priority === p),
  }))

  const hasAny =
    plan.recommendations.length > 0 ||
    plan.strengths.length > 0 ||
    plan.next_promotion_blockers.length > 0

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">
        <Target className="h-4 w-4 text-indigo-400" />
        Coaching
      </h2>

      {plan.headline && (
        <h3 className="mb-4 text-base font-semibold text-slate-100">
          {plan.headline}
        </h3>
      )}

      {!hasAny ? (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-800/60 bg-emerald-950/30 p-4 text-sm text-emerald-200">
          <Sparkles className="h-4 w-4 text-emerald-400" />
          <span>No coaching recommendations — keep it up!</span>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(
            ({ priority, recs }) =>
              recs.length > 0 && (
                <div key={priority} className="space-y-2">
                  <h4
                    className={`text-xs font-semibold uppercase tracking-wide ${PRIORITY_STYLES[priority].heading}`}
                  >
                    {PRIORITY_LABELS[priority]}
                  </h4>
                  <ul className="space-y-2">
                    {recs.map((r) => (
                      <li
                        key={`${priority}-${r.title}`}
                        className={`rounded-lg border-l-4 ${PRIORITY_STYLES[priority].border} border-y border-r border-slate-800 bg-slate-950/40 p-3`}
                      >
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <span className="text-sm font-bold text-slate-100">
                            {r.title}
                          </span>
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${PRIORITY_STYLES[priority].badge}`}
                          >
                            P{priority}
                          </span>
                          {r.metric && (
                            <span className="rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] text-slate-400">
                              {r.metric}
                            </span>
                          )}
                        </div>
                        <p className="mb-1.5 text-xs text-slate-400">{r.why}</p>
                        <p className="text-sm text-slate-200">{r.action}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )
          )}

          {plan.strengths.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-300">
                Strengths
              </h4>
              <ul className="space-y-1">
                {plan.strengths.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-2 text-sm text-slate-200"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {plan.next_promotion_blockers.length > 0 && (
            <div className="rounded-lg border border-amber-900/60 bg-amber-950/20 p-3">
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5" />
                Next promotion blockers
              </h4>
              <ul className="list-disc space-y-1 pl-5 text-sm text-amber-100">
                {plan.next_promotion_blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
